import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdOutlineAccountBalance,
  MdCheckCircle,
  MdRadioButtonUnchecked,
} from "react-icons/md";
import {
  FaSearch,
  FaArrowRight,
  FaCalendarAlt,
  FaUser,
  FaTimes,
  FaChevronDown,
  FaExclamationTriangle,
  FaLock,
  FaCheckCircle,
} from "react-icons/fa";
import { HiOutlineDocumentText } from "react-icons/hi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "js-cookie";

// ─── Employee Search Dropdown ─────────────────────────────────────────────────
const EmployeeSearchDropdown = ({ value, onSelect, error, required = false }) => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const token = Cookies.get("user_token");

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchEmployees = useCallback(
    async (search) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("limit", "20");
        if (search?.trim()) params.set("search", search.trim());

        const res = await fetch(
          `${API_URL}/v1/hris/employees/employee/all-details?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        let result = {};
        try { result = await res.json(); } catch { result = {}; }

        if (!res.ok || !result.success) { setResults([]); return; }
        setResults(Array.isArray(result.data) ? result.data : []);
      } catch (err) {
        console.error("Employee fetch error:", err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [API_URL, token]
  );

  const handleOpen = () => {
    setOpen((prev) => !prev);
    if (!open && results.length === 0 && !selected) fetchEmployees("");
  };

  const handleSelect = (emp) => {
    setSelected(emp);
    setQuery("");
    setOpen(false);
    onSelect(emp.employee_no, emp);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    onSelect("", null);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { fetchEmployees(val); }, 350);
  };

  return (
    <div>
      <label className="block text-sm text-gray-700 mb-1">
        Employee
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div ref={dropdownRef} className="relative">
        {selected ? (
          <div className="flex items-center gap-3 border-2 rounded-lg px-3 py-2.5 bg-blue-50 border-blue-300 transition-all">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
              {(selected.employee_calling_name || selected.employee_fullname || "?")[0]?.toUpperCase?.() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {selected.employee_calling_name || selected.employee_fullname}
                <span className="ml-2 text-xs font-mono text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                  {selected.employee_no}
                </span>
              </p>
              <p className="text-xs text-gray-500 truncate">
                {selected.designation_name} · {selected.department_name}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-600 flex items-center justify-center text-gray-500 transition-colors"
              title="Change employee"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleOpen}
            className={`w-full flex items-center justify-between border ${
              error ? "border-red-500 bg-red-50" : "border-gray-300 bg-white"
            } px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm`}
          >
            <div className="flex items-center gap-2 text-gray-600">
              <FaUser className="text-gray-400 text-sm" />
              <span>Select employee</span>
            </div>
            <div className="flex items-center gap-2">
              {loading ? (
                <svg className="animate-spin h-4 w-4 text-blue-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <FaChevronDown className="text-gray-400 text-xs" />
              )}
            </div>
          </button>
        )}

        <AnimatePresence>
          {open && !selected && (
            <motion.div
              initial={{ opacity: 0, y: -6, scaleY: 0.95 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              exit={{ opacity: 0, y: -6, scaleY: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
              style={{ transformOrigin: "top" }}
            >
              <div className="p-3 border-b border-gray-100 bg-white">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                  <input
                    type="text"
                    value={query}
                    onChange={handleSearchChange}
                    placeholder="Search by name, employee no, NIC…"
                    className="w-full border border-gray-300 pl-9 pr-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm"
                  />
                </div>
              </div>

              {loading && results.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Searching employees…
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                  <FaUser className="text-gray-300" />
                  No employees found
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
                  {results.map((emp) => (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={() => handleSelect(emp)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left group"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 group-hover:from-blue-500 group-hover:to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold transition-all">
                        {(emp.employee_calling_name || emp.employee_fullname || "?")[0]?.toUpperCase?.() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800 truncate">
                            {emp.employee_calling_name || emp.employee_fullname}
                          </span>
                          <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                            {emp.employee_no}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                            emp.employee_active_status === "Active"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }`}>
                            {emp.employee_active_status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {emp.designation_name} · {emp.department_name}
                          {emp.epf_no && <span className="ml-2 text-gray-300">EPF: {emp.epf_no}</span>}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

// ─── Loan Card ────────────────────────────────────────────────────────────────
const LoanCard = ({ loan, isSelected, onToggle }) => {
  const hasSchedule = !!loan?.schedule_file_url;
  const rawAmount = parseFloat(loan?.loan_amount);
  const loanAmount = isNaN(rawAmount)
    ? "—"
    : rawAmount.toLocaleString("en-LK", { style: "currency", currency: "LKR", maximumFractionDigits: 0 });

  const handleClick = () => {
    if (!hasSchedule) {
      toast.warning("This loan has no schedule uploaded. Please upload a schedule first.");
      return;
    }
    onToggle();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={handleClick}
      className={`relative rounded-xl border-2 p-5 transition-all duration-300 ${
        !hasSchedule
          ? "cursor-not-allowed opacity-60 border-gray-200 bg-white"
          : isSelected
          ? "cursor-pointer border-blue-500 bg-blue-50 shadow-md shadow-blue-100"
          : "cursor-pointer border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm"
      }`}
    >
      <div className="absolute top-4 right-4">
        {hasSchedule ? (
          isSelected ? <MdCheckCircle className="text-blue-500 text-2xl" /> : <MdRadioButtonUnchecked className="text-gray-300 text-2xl" />
        ) : (
          <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3 pr-8">
        <div className={`p-2 rounded-lg ${isSelected ? "bg-blue-100" : "bg-gray-100"}`}>
          <MdOutlineAccountBalance className={`text-lg ${isSelected ? "text-blue-600" : "text-gray-500"}`} />
        </div>
        <div>
          <p className="font-semibold text-gray-800 text-sm">{loan?.loan_reference_no ?? "—"}</p>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            loan?.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
          }`}>
            {loan?.status ?? "—"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-y-2 text-xs text-gray-600">
        <div><p className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Loan Amount</p><p className="font-semibold text-gray-800">{loanAmount}</p></div>
        <div><p className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Bank ID</p><p className="font-semibold text-gray-800">{loan?.loan_bank_id ?? "—"}</p></div>
        <div><p className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Start Date</p><p className="font-semibold text-gray-800">{loan?.loan_start_date ?? "—"}</p></div>
        <div><p className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">End Date</p><p className="font-semibold text-gray-800">{loan?.loan_end_date ?? "—"}</p></div>
      </div>

      <div className={`mt-3 pt-3 border-t ${isSelected ? "border-blue-200" : "border-gray-100"} flex items-center gap-1.5 text-xs`}>
        <HiOutlineDocumentText className={hasSchedule ? "text-green-500" : "text-red-300"} />
        <span className={hasSchedule ? "text-green-600 font-medium" : "text-red-400 font-medium"}>
          {hasSchedule ? "Schedule uploaded" : "⛔ Upload schedule first to select"}
        </span>
      </div>
    </motion.div>
  );
};

// ─── Confirm Save Modal ───────────────────────────────────────────────────────
const ConfirmSaveModal = ({ isOpen, onClose, onConfirm, saving, amount, month, employeeName }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 280, damping: 24 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-800 to-blue-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <FaExclamationTriangle className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg leading-tight">Confirm & Save Reimbursement</h3>
                <p className="text-white/80 text-xs mt-0.5">This action cannot be undone</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Amount summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">You are saving</p>
              <p className="text-2xl font-black text-emerald-600">{amount}</p>
              <p className="text-xs text-gray-500 mt-1">
                for <span className="font-semibold text-gray-700">{employeeName}</span> · {month}
              </p>
            </div>

            {/* Proceed question */}
            <p className="text-sm text-gray-700 font-medium mb-4">
              Proceed to save this reimbursement amount to the database?
            </p>

            {/* ⚠ Red irreversible warning */}
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-5">
              <div className="flex items-start gap-2.5">
                <FaLock className="text-red-500 text-sm flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-700 mb-1">Important — Please read carefully</p>
                  <ul className="text-xs text-red-600 space-y-1.5 list-none">
                    <li className="flex items-start gap-1.5">
                      <span className="text-red-400 font-bold flex-shrink-0 mt-0.5">✕</span>
                      <span>Once saved, this reimbursement <strong>cannot be run again</strong> for <strong className="whitespace-nowrap">{month}</strong>.</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <span className="text-red-400 font-bold flex-shrink-0 mt-0.5">✕</span>
                      <span>This action is <strong>permanent and cannot be undone</strong>.</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <span className="text-red-400 font-bold flex-shrink-0 mt-0.5">✕</span>
                      <span>Contact your system administrator if a correction is needed after saving.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={saving}
                className={`flex-1 py-2.5 rounded-lg text-sm border border-gray-300 text-gray-600 hover:bg-gray-50 transition font-medium ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={saving}
                className={`flex-1 py-2.5 rounded-lg text-sm text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 transition font-semibold flex items-center justify-center gap-2 shadow-md shadow-green-200 ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Saving…
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="text-sm" />
                    Yes, Save Reimbursement
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Reimbursement Result Card ─────────────────────────────────────────────────
const ReimbursementResult = ({ result, month, employeeName, onConfirmSave, saved }) => {
  if (!result) return null;

  const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return "LKR 0.00";
    return n.toLocaleString("en-LK", { style: "currency", currency: "LKR", minimumFractionDigits: 2 });
  };

  // Preview API:  { success, preview: { loans, totals, result: { reimbursement, case } } }
  // Confirm API:  { success, data:    { loans, totals, result: { reimbursement, case } } }
  const payload = result?.preview ?? result?.data;

  const reimbursement = payload?.result?.reimbursement ?? 0;
  const interest      = payload?.totals?.totalMonthlyInterest ?? 0;
  const totalLoan     = payload?.totals?.totalSelectedLoanAmount ?? 0;
  const caseLabel     = payload?.result?.case ?? "—";
  const loans         = Array.isArray(payload?.loans) ? payload.loans : [];


  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="mt-6 rounded-2xl overflow-hidden border border-blue-100 shadow-lg"
    >
      {/* ── Preview-only hint banner ── */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2">
        <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <p className="text-xs text-amber-700 font-medium">
          <span className="font-bold">Preview only</span> — This amount has not been saved yet. Review the figures below and confirm to record it.
        </p>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
        <p className="text-white/80 text-xs font-medium uppercase tracking-widest mb-1">Reimbursement Preview</p>
        <p className="text-white font-bold text-lg">{month}</p>
      </div>

      {/* Stats */}
      <div className="bg-white p-5 grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Total Loan</p>
          <p className="text-gray-800 font-bold text-sm">{fmt(totalLoan)}</p>
        </div>
        <div className="text-center border-x border-gray-100">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Monthly Interest</p>
          <p className="text-gray-800 font-bold text-sm">{fmt(interest)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Case</p>
          <p className="text-blue-600 font-bold text-sm">{caseLabel}</p>
        </div>
      </div>

      {/* Hero amount */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-t border-green-100 px-6 py-5 text-center">
        <p className="text-gray-500 text-xs font-medium uppercase tracking-widest mb-1">Reimbursement Amount</p>
        <p className="text-4xl font-black text-emerald-600">{fmt(reimbursement)}</p>
      </div>

      {/* Loans breakdown */}
      {loans.length > 0 && (
        <div className="bg-gray-50 border-t border-gray-100 px-5 py-4">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-3">Included Loans</p>
          {loans.map((loan, i) => (
            <div key={loan?.id ?? i} className="flex items-center justify-between text-sm mb-1.5">
              <span className="font-mono text-blue-600 text-xs bg-blue-50 px-2 py-0.5 rounded">{loan?.ref ?? loan?.loan_reference_no ?? "—"}</span>
              <span className="text-gray-600 text-xs">Interest: {fmt(loan?.interest ?? loan?.interest_amount ?? 0)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Confirm Save section ── */}
      <div className="border-t border-gray-100 px-5 py-4 bg-white">
        {saved ? (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <FaCheckCircle className="text-white text-sm" />
            </div>
            <div>
              <p className="text-sm font-semibold text-green-800">Reimbursement Saved Successfully</p>
              <p className="text-xs text-green-600 mt-0.5">
                This reimbursement for <strong>{month}</strong> has been recorded and cannot be modified.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <FaExclamationTriangle className="text-red-500 text-sm flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">
                <span className="font-bold">Warning:</span> Once you save this reimbursement, it{" "}
                <strong>cannot be run again for {month}</strong> and <strong>cannot be undone</strong>.
                Ensure all figures are correct before proceeding.
              </p>
            </div>
            <button
              onClick={onConfirmSave}
              className="w-full py-3 rounded-xl text-sm text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 transition-all font-semibold flex items-center justify-center gap-2 shadow-md shadow-green-100 hover:shadow-green-200"
            >
              <FaCheckCircle className="text-sm" />
              Confirm &amp; Save Reimbursement
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SelectBankLoan = () => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const token = Cookies.get("user_token");
  const createdBy = Cookies.get("user_email") || "SYSTEM";

  const [employeeNo, setEmployeeNo] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeError, setEmployeeError] = useState("");

  const [loans, setLoans] = useState([]);
  const [selectedLoanIds, setSelectedLoanIds] = useState([]);
  const [selectionLoading, setSelectionLoading] = useState(false);

  const [reimbMonth, setReimbMonth] = useState("");
  const [monthError, setMonthError] = useState("");
  const [runLoading, setRunLoading] = useState(false);
  const [reimbResult, setReimbResult] = useState(null);
  const [loansLoading, setLoansLoading] = useState(false);

  // ── Confirm/Save state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loansRef = useRef(null);
  const monthRef = useRef(null);

  // ── Auto-fetch loans when employee is selected
  const handleEmployeeSelect = async (empNo, empObj) => {
    setEmployeeNo(empNo);
    setSelectedEmployee(empObj);
    setEmployeeError("");
    setLoans([]);
    setSelectedLoanIds([]);
    setReimbResult(null);
    setSaved(false);

    if (!empNo) return;

    setLoansLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/v1/hris/BankLoanReimbursement/reimbursements/employees/${empNo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let result = {};
      try { result = await res.json(); } catch { result = {}; }

      if (!res.ok || !result.success) {
        toast.error(result?.message || `Error ${res.status}: Failed to fetch loans.`);
        return;
      }

      const fetchedLoans = Array.isArray(result.data) ? result.data : [];
      if (fetchedLoans.length === 0) { toast.info("No loans found for this employee."); return; }

      setLoans(fetchedLoans);
      setTimeout(() => loansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      toast.success(`Found ${fetchedLoans.length} loan(s) for ${empNo}`);
    } catch (err) {
      console.error("Loans fetch error:", err);
      toast.error("Failed to fetch loans. Please try again.");
    } finally {
      setLoansLoading(false);
    }
  };

  const toggleLoan = (id) => {
    if (id == null) return;
    setSelectedLoanIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setReimbResult(null);
    setSaved(false);
  };

  // ── Step 1 + 2: Select loans → Preview amount
  const handleConfirmAndRun = async () => {
    if (!employeeNo) { setEmployeeError("Please select an employee."); return; }
    if (selectedLoanIds.length === 0) { toast.error("Please select at least one loan."); return; }
    if (!reimbMonth) { setMonthError("Please select a reimbursement month."); monthRef.current?.focus(); return; }

    setMonthError("");
    setReimbResult(null);
    setSaved(false);
    setSelectionLoading(true);

    try {
      // 1. Save selection
      const selPayload = {
        selection_month: reimbMonth,
        request_type: "BANK_LOAN_REIMB",
        remarks: `Selection for ${reimbMonth}`,
        selections: selectedLoanIds.map((id) => ({ bank_loan_id: id })),
      };

      const selRes = await fetch(
        `${API_URL}/v1/hris/BankLoanReimbursement/employees/${employeeNo}/selection`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(selPayload),
        }
      );
      let selResult = {};
      try { selResult = await selRes.json(); } catch { selResult = {}; }

      if (!selRes.ok || !selResult.success) {
        toast.error(selResult?.message || `Selection failed: ${selRes.status}`);
        return;
      }

      // 2. Preview (does NOT save to DB)
      setRunLoading(true);
      const runPayload = { employee_no: employeeNo, reimb_month: reimbMonth, created_by: createdBy };

      const runRes = await fetch(
        `${API_URL}/v1/hris/BankLoanReimbursement/bank-loan-payroll/preview-employee-month`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(runPayload),
        }
      );
      let runResult = {};
      try { runResult = await runRes.json(); } catch { runResult = {}; }

      if (!runRes.ok || !runResult.success) {
        toast.error(runResult?.message || `Preview failed: ${runRes.status}`);
        return;
      }

      setReimbResult(runResult);
      toast.success("Preview calculated. Review the amount before saving.");
      setTimeout(() => monthRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch (err) {
      console.error("Preview error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSelectionLoading(false);
      setRunLoading(false);
    }
  };

  // ── Step 3: Confirm & save to DB
  const handleSaveConfirmed = async () => {
    setSaving(true);
    try {
      const payload = { employee_no: employeeNo, reimb_month: reimbMonth, created_by: createdBy };

      const res = await fetch(
        `${API_URL}/v1/hris/BankLoanReimbursement/bank-loan-payroll/confirm-employee-month`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        }
      );
      let result = {};
      try { result = await res.json(); } catch { result = {}; }

      if (!res.ok || !result.success) {
        toast.error(result?.message || `Save failed: ${res.status}`);
        return;
      }

      setShowConfirmModal(false);
      setSaved(true);
      toast.success("Reimbursement saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return "LKR 0.00";
    return n.toLocaleString("en-LK", { style: "currency", currency: "LKR", minimumFractionDigits: 2 });
  };

  // preview → .preview.result.reimbursement  |  confirm → .data.result.reimbursement
  const reimbursementAmount = (reimbResult?.preview ?? reimbResult?.data)?.result?.reimbursement ?? 0;
  const isLoading = selectionLoading || runLoading;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8 font-montserrat">
        <div className="mb-8 flex items-center gap-3">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-xl">
            <MdOutlineAccountBalance className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-[25px] font-semibold text-slate-800">Select Bank Loan</h1>
            <p className="text-gray-500 text-sm">Choose loans and calculate reimbursement</p>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-2xl p-8 w-full">

          {/* ── Section 1: Employee Search ── */}
          <h2 className="text-xl font-semibold text-gray-800 mb-1">Employee Details</h2>
          <p className="text-gray-500 text-sm mb-6">
            Search and select an employee to fetch their active bank loans.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div>
              <EmployeeSearchDropdown
                value={employeeNo}
                onSelect={handleEmployeeSelect}
                error={employeeError}
                required
              />
            </div>

            <AnimatePresence>
              {selectedEmployee && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="bg-slate-50 border border-slate-200 rounded-xl p-4"
                >
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Employee Info</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div><p className="text-gray-400 mb-0.5">Full Name</p><p className="font-semibold text-gray-700">{selectedEmployee.employee_fullname?.trim() || "—"}</p></div>
                    <div><p className="text-gray-400 mb-0.5">NIC</p><p className="font-semibold text-gray-700">{selectedEmployee.employee_nic || "—"}</p></div>
                    <div><p className="text-gray-400 mb-0.5">Department</p><p className="font-semibold text-gray-700">{selectedEmployee.department_name || "—"}</p></div>
                    <div><p className="text-gray-400 mb-0.5">Designation</p><p className="font-semibold text-gray-700">{selectedEmployee.designation_name || "—"}</p></div>
                    <div><p className="text-gray-400 mb-0.5">EPF No</p><p className="font-semibold text-gray-700">{selectedEmployee.epf_no || "—"}</p></div>
                    <div>
                      <p className="text-gray-400 mb-0.5">Status</p>
                      <span className={`px-2 py-0.5 rounded-full font-medium ${selectedEmployee.employee_active_status === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {selectedEmployee.employee_active_status || "—"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {loansLoading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-6 flex items-center gap-2 text-blue-600 text-sm">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Fetching loans…
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Section 2: Loan Cards ── */}
          <AnimatePresence>
            {loans.length > 0 && (
              <motion.div ref={loansRef} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800">Available Loans</h2>
                    <p className="text-gray-500 text-sm">
                      {loans.length} loan{loans.length !== 1 ? "s" : ""} found for{" "}
                      <span className="font-medium text-blue-600">{selectedEmployee?.employee_calling_name || employeeNo}</span>. Only loans with a schedule can be selected.
                    </p>
                  </div>
                  {selectedLoanIds.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {selectedLoanIds.length} selected
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {loans.map((loan) => (
                    <LoanCard
                      key={loan?.id}
                      loan={loan}
                      isSelected={selectedLoanIds.includes(loan?.id)}
                      onToggle={() => toggleLoan(loan?.id)}
                    />
                  ))}
                </div>

                {/* ── Section 3: Month + Calculate ── */}
                <AnimatePresence>
                  {selectedLoanIds.length > 0 && (
                    <motion.div
                      ref={monthRef}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 16 }}
                      transition={{ duration: 0.35 }}
                      className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-200"
                    >
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">Reimbursement Month</h3>
                      <p className="text-gray-500 text-sm mb-4">
                        Select the month to calculate reimbursement for{" "}
                        <span className="font-medium text-blue-600">{selectedLoanIds.length}</span> selected loan(s).
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                        <div className="md:col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">
                            Month <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                              type="month"
                              value={reimbMonth}
                              onChange={(e) => { setReimbMonth(e.target.value); setMonthError(""); setReimbResult(null); setSaved(false); }}
                              className={`w-full border ${monthError ? "border-red-500 bg-red-50" : "border-gray-300"} pl-9 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 transition`}
                            />
                          </div>
                          {monthError && <p className="text-red-500 text-sm mt-1">{monthError}</p>}
                        </div>

                        <button
                          onClick={handleConfirmAndRun}
                          disabled={isLoading || saved}
                          className={`h-[42px] flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium shadow-md shadow-blue-200 ${
                            isLoading || saved ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                              </svg>
                              {selectionLoading ? "Selecting..." : "Calculating..."}
                            </>
                          ) : (
                            <>Calculate <FaArrowRight className="text-sm" /></>
                          )}
                        </button>
                      </div>

                      {/* Result card with preview hint + confirm save */}
                      <AnimatePresence>
                        {reimbResult && (
                          <ReimbursementResult
                            result={reimbResult}
                            month={reimbMonth}
                            employeeName={selectedEmployee?.employee_calling_name || selectedEmployee?.employee_fullname || employeeNo}
                            onConfirmSave={() => setShowConfirmModal(true)}
                            saved={saved}
                          />
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Confirm Save Modal ── */}
      <ConfirmSaveModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleSaveConfirmed}
        saving={saving}
        amount={fmt(reimbursementAmount)}
        month={reimbMonth}
        employeeName={selectedEmployee?.employee_calling_name || selectedEmployee?.employee_fullname || employeeNo}
      />

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </motion.div>
  );
};

export default SelectBankLoan;